import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Event, NavigationEnd, Router, RouterLink } from '@angular/router';
import { faHouse } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import {
  BreadcrumbsService,
  ICurrentBreadcrumbItem
} from '../../services/breadcrumbs/breadcrumbs.service';
import { Title } from '@angular/platform-browser';
import { convertHtmlToPlainText } from 'src/app/misc.utils';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

export interface IBreadcrumbLink {
  text?: string;
  url?: string;
  isPlaceholder?: boolean;
}

@Component({
    selector: 'app-header-menu-breadcrumbs',
    templateUrl: './header-menu-breadcrumbs.component.html',
    styleUrls: ['./header-menu-breadcrumbs.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink, FaIconComponent, TranslateModule]
})
export class HeaderMenuBreadcrumbsComponent implements OnInit, OnDestroy {
  faHouse = faHouse;

  breadcrumbLinks: IBreadcrumbLink[] = [];
  currentRoute = '/';

  itemLabels: {
    [itemId: string]: string;
  } = {};

  manualBreadcrumbItems: IBreadcrumbLink[] = [];

  private subscriptions = new Subscription();
  private updateLinksQueued = false;

  constructor(
    private router: Router,
    private breadcrumbsService: BreadcrumbsService,
    private titleService: Title,
    private translateService: TranslateService
  ) {
    this.updateLinks();

    this.router.events.subscribe((event: Event) => {
      this.currentRoute = window.location.pathname;

      if (event instanceof NavigationEnd) {
        // Hide progress spinner or progress bar
        this.currentRoute = event.url;

        this.manualBreadcrumbItems = [];
      }

      this.queueUpdateLinks();
    });

    this.subscriptions.add(
      this.translateService.onLangChange.subscribe(() => {
        this.queueUpdateLinks();
      })
    );
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      this.subscriptions.add(
        this.breadcrumbsService.currentItemObservable.subscribe(
          (currentItem: ICurrentBreadcrumbItem | null) => {
            if (currentItem) {
              this.itemLabels[currentItem.id] = currentItem.text;
            }

            this.currentRoute = window.location.pathname;

            this.queueUpdateLinks();
          }
        )
      );

      this.subscriptions.add(
        this.breadcrumbsService.manualBreadcrumbItemsObservable.subscribe(
          (manualBreadcrumbItems: IBreadcrumbLink[]) => {
            this.manualBreadcrumbItems = manualBreadcrumbItems;

            this.currentRoute = window.location.pathname;

            this.queueUpdateLinks();
          }
        )
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  queueUpdateLinks(): void {
    if (this.updateLinksQueued) {
      return;
    }

    this.updateLinksQueued = true;

    Promise.resolve().then(() => {
      this.updateLinksQueued = false;
      this.updateLinks();
    });
  }

  updateLinks(): void {
    this.breadcrumbLinks = [];

    if (this.manualBreadcrumbItems.length) {
      for (const prefixItem of this.manualBreadcrumbItems) {
        this.breadcrumbLinks.push(prefixItem);
      }
    } else {
      const splittedRoute: string[] = this.currentRoute.split(/[?#]/, 1)[0].split('/');

      for (let i = 2; i < splittedRoute.length; i++) {
        let text: string | null = null;

        switch (splittedRoute[i]) {
          case 'dashboard':
            text = 'GENERAL.HOME';
            break;
          case 'flight-calendar':
            text = 'FLIGHTS_CALENDAR.TITLE';
            break;
          case 'statistics':
            text = 'BREADCRUMBS.STATISTICS';
            break;
          case 'my-account':
            text = 'BREADCRUMBS.MY_ACCOUNT';
            break;
          case 'finance':
            text = 'BREADCRUMBS.FINANCE';
            break;
          case 'clients':
            text = 'CLIENTS.PLURAL';
            break;
          case 'suppliers':
            text = 'SUPPLIERS.PLURAL';
            break;
          case 'airlines':
            text = 'AIRLINES.PLURAL';
            break;
          case 'encaissements':
            text = 'ENCAISSEMENTS.PLURAL';
            break;
          case 'aircraft-models':
            text = 'AIRCRAFT_MODELS.PLURAL';
            break;
          case 'aircrafts':
            text = 'AIRCRAFTS.PLURAL';
            break;
          case 'simulateur-aircraft-models':
            text = 'SIMULATOR.AIRCRAFT_MODELS';
            break;
          case 'lieux-dits':
            text = 'BREADCRUMBS.LIEUX_DITS';
            break;
          case 'email-templates':
            text = 'BREADCRUMBS.EMAIL_TEMPLATES';
            break;
          case 'user-groups':
            text = 'BREADCRUMBS.USER_GROUPS';
            break;
          case 'encaissement-types':
            text = 'BREADCRUMBS.ENCAISSEMENT_TYPES';
            break;
          case 'airports':
            text = 'AIRPORTS.PLURAL';
            break;
          case 'airport':
            text = 'BREADCRUMBS.AIRPORT';
            break;
          case 'enquiries':
            text = 'ENQUIRIES.PLURAL';
            break;
          case 'enquiry':
            text = 'ENQUIRIES.SINGULAR';
            break;
          case 'bank-accounts':
            text = 'BANK_ACCOUNTS.PLURAL';
            break;
          case 'api-tokens':
            text = 'BREADCRUMBS.API_TOKENS';
            break;
          case 'flight-brief-caterings':
            text = 'BREADCRUMBS.FLIGHT_BRIEF_CATERINGS';
            break;
          case 'crawler-urls':
            text = 'BREADCRUMBS.CRAWLER_URLS';
            break;
          case 'backups':
            text = 'BREADCRUMBS.BACKUPS';
            break;
          case 'countries':
            text = 'COUNTRIES.PLURAL';
            break;
          case 'flight-checklist-templates':
            text = 'FLIGHT_CHECKLIST_TEMPLATES.PLURAL';
            break;
          case 'airline-supervisors':
            text = 'BREADCRUMBS.AIRLINE_SUPERVISORS';
            break;
          case 'airport-supervisors':
            text = 'BREADCRUMBS.AIRPORT_SUPERVISORS';
            break;
          case 'supervisors':
            text = 'BREADCRUMBS.SUPERVISORS';
            break;
          case 'crews':
            text = 'BREADCRUMBS.CREWS';
            break;
          case 'fbos':
            text = 'BREADCRUMBS.FBOS';
            break;
          case 'handlings':
            text = 'BREADCRUMBS.HANDLINGS';
            break;
          case 'employee-bonus-rules':
            text = 'BREADCRUMBS.EMPLOYEE_BONUS_RULES';
            break;
          case 'flight-briefs':
            text = 'BREADCRUMBS.FLIGHT_BRIEFS';
            break;
          case 'users':
            text = 'USERS.PLURAL';
            break;
          case 'invoices':
            text = 'BREADCRUMBS.INVOICES';
            break;
          case 'ai-purchase':
            text = 'INVOICE.AI.TITLE';
            break;
          case 'add-purchase-invoice':
            text = 'INVOICE.NEW_PURCHASE';
            break;
          case 'add-purchase-credit-note':
            text = 'INVOICE.NEW_PURCHASE_CREDIT_NOTE';
            break;
          case 'add-purchase-proforma':
            text = 'INVOICE.NEW_PURCHASE_PROFORMA';
            break;
          case 'add-purchase-proforma-credit-note':
            text = 'INVOICE.NEW_PURCHASE_PROFORMA_CREDIT_NOTE';
            break;
          case 'quotations':
            text = 'QUOTATION.PLURAL';
            break;
          case 'export-requests':
            text = 'EXPORT_REQUESTS.PLURAL';
            break;
          case 'list':
            text = 'GENERAL.LIST';
            break;
          case 'add':
            text = 'GENERAL.ADD';
            break;
          case 'view':
            text = 'GENERAL.VIEW';
            break;
          case 'edit':
            text = 'GENERAL.EDIT';
            break;
          case 'import':
            text = 'GENERAL.IMPORT';
            break;
          case 'list-no-capacity':
            text = 'BREADCRUMBS.LIST_NO_CAPACITY';
            break;
          case 'edit-password':
            text = 'BREADCRUMBS.EDIT_PASSWORD';
            break;
          case 'relance-airline':
            text = 'BREADCRUMBS.RELANCE_AIRLINE';
            break;
          case 'select-airlines':
            text = 'BREADCRUMBS.SELECT_AIRLINES';
            break;
          case 'send-message-to-airlines':
            text = 'BREADCRUMBS.SEND_MESSAGE_TO_AIRLINES';
            break;
          case 'cancel-email-airline':
            text = 'BREADCRUMBS.CANCEL_EMAIL_AIRLINE';
            break;
          case 'blank-email':
            text = 'BREADCRUMBS.BLANK_EMAIL';
            break;
          case 'settings':
            text = 'BREADCRUMBS.SETTINGS';
            break;
          case 'ai':
            text = splittedRoute[i - 1] === 'settings' ? 'SETTINGS_AI.TITLE' : 'ai';
            break;
          case 'api':
            text = 'BREADCRUMBS.API';
            break;
          case 'logs':
            text = 'BREADCRUMBS.LOGS';
            break;
          case 'duplicates':
            text = 'BREADCRUMBS.DUPLICATES';
            break;
          case 'import-contacts':
            text = 'BREADCRUMBS.IMPORT_CONTACTS';
            break;
          case 'cfmu':
            text = 'ENQUIRIES.CFMU';
            break;
          case 'send-emails':
            text = 'BREADCRUMBS.SEND_EMAILS';
            break;
          case 'invoice-requested':
            text = 'BREADCRUMBS.INVOICE_REQUESTED';
            break;
          default:
            if (splittedRoute[i - 1] === 'ai-purchase') {
              text = 'INVOICE.AI.BATCH';
            } else if (typeof this.itemLabels[splittedRoute[i]] === 'undefined') {
              text = splittedRoute[i];
            } else if (typeof this.itemLabels[splittedRoute[i]] !== 'undefined') {
              text = this.itemLabels[splittedRoute[i]];
            }

            break;
        }

        if (text) {
          let url = '';

          for (let j = 0; j <= i; j++) {
            url += '/' + splittedRoute[j];
          }

          this.breadcrumbLinks.push({
            // Resolve generated route labels immediately so a refresh cannot render
            // a stale fallback while the translation pipe is waiting for the catalog.
            text: this.translateService.instant(text),
            url
          });
        }
      }
    }

    const titleArr: string[] = [];

    if (this.breadcrumbLinks.length) {
      for (const breadcrumbLink of this.breadcrumbLinks) {
        titleArr.push(this.getTranslatedTitlePart(breadcrumbLink.text));
      }
    }

    this.updateCurrentTitle(titleArr.join(' - '));
  }

  updateCurrentTitle(text: string | null): void {
    this.titleService.setTitle(text ? 'Simplyfly - ' + convertHtmlToPlainText(text) : 'Simplyfly');
  }

  getTranslatedTitlePart(text: string | null | undefined): string {
    if (!text) {
      return '';
    }

    const translatedText = this.translateService.instant(text);

    return translatedText && translatedText !== text ? translatedText : text;
  }
}
